import { useFrontendConfigContext } from '@apeiron/config'
import { CurrencyTokenType, useCurrencyContext } from '@apeiron/currency'
import {
  ApeironBlueButton,
  ApeironTextField,
  InfoPopup,
  useWeb3
} from '@apeiron/temp'
import { ApeironCheckbox, ApeironNumberTextField } from '@apeiron/ui'
import { Box, Container, styled, Typography } from '@mui/material'
import RentalSettingInput from '@src/components/orbitalTrack/rentalSetting/common/RentalSettingInput'
import WhitelistEdit from '@src/components/orbitalTrack/rentalSetting/inviteRental/WhitelistEdit'
import ApeironBorderButton from '@src/components/share/apeiron/ApeironBorderButton'
import ApeironSwitch from '@src/components/share/apeiron/ApeironSwitch'
import { FeatureFlagType } from '@src/constants/featureFlag'
import { useOrbitalTrackAttachContext } from '@src/contexts/orbitalTrack/OrbitalTrackAttachContext'
import useSaveRentalSetting from '@src/hooks/contract/useSaveRentalSetting'
import useGetOrbitalTrackSuggestFee from '@src/hooks/graphql/useGetOrbitalTrackSuggestFee'
import featureAllowed from '@src/util/apeiron/featureAllowed'
import Decimal from 'decimal.js'
import * as R from 'ramda'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

const Popup = styled(InfoPopup)`
  background: #171719;
  border-radius: unset;
  max-height: 96%;
  max-width: 700px;
  width: 96%;
  .Custom-Content {
    display: flex;
    flex-direction: column;
    margin: -50px 0px 0px 0px;
    width: 100%;
  }
`

const Head = styled(Box)`
  background-color: #262829;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  ${props => props.theme.breakpoints.up('res1024')} {
    padding: 30px 70px 30px 30px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    padding: 20px 50px 20px 20px;
  }
`

const Title = styled(Typography)`
  color: white;
  font-size: 20px;
  font-weight: 500;
  line-height: 20px;
`

const Description = styled(Typography)`
  color: #838383;
  overflow-wrap: break-word;
`

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
  overflow-y: auto;
  ${props => props.theme.breakpoints.up('res1024')} {
    padding: 30px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    padding: 20px;
  }
`

const RentLabel = styled(Typography)`
  border-radius: 4px;
  font-size: 14px;
  font-weight: 300;
  line-height: 14px;
  padding: 5px 10px;
  strong {
    font-weight: 500;
  }
`

const OrangeRentLabel = styled(RentLabel)`
  background-color: #ffb10b;
  color: black;
`

const GreenRentLabel = styled(RentLabel)`
  background-color: #429700;
  color: white;
`

const StyledApeironCheckbox = styled(ApeironCheckbox)`
  .Custom-Description {
    ${props => props.theme.breakpoints.down('res1280')} {
      font-size: 14px;
      line-height: 18px;
      max-width: unset;
    }
  }
`

const AllowRenewSwitch = styled(ApeironSwitch)`
  align-self: flex-end;
`

const ApplyButton = styled(ApeironBlueButton)`
  align-self: flex-end;
  font-size: 16px;
  font-weight: 400;
  padding: 10px 30px;
`

const Remark = styled(Typography)`
  color: #838383;
  text-align: right;
  strong {
    color: white;
  }
`

const MinMax = styled(Remark)`
  strong {
    color: #ffbf36;
  }
`

const NameTextField = styled(ApeironTextField)`
  input:-webkit-autofill {
    -webkit-text-fill-color: white;
  }
`

const DeleteButton = styled(ApeironBorderButton)`
  border: 2px solid #6c3333;
  background-color: #470000;
  font-weight: 300;
  width: 100%;
  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 14px;
    padding: 0px 10px;
  }

  :disabled {
    background-color: #2d0000;
    pointer-events: none;
    color: #838383;
  }

  :hover {
    background-color: #7a0303;
  }
`

const DeleteSection = styled(Container)`
  display: flex;
  background-color: #470000;
  padding: 12px 30px;
  align-items: center;
`

const DeleteSectionLeftBox = styled(Box)`
  flex: 0.7;
`

const DeleteSectionRightBox = styled(Box)`
  flex: 0.3;
`

const Unit = Typography

export enum RentalType {
  PUBLIC = 'public',
  INVITE = 'invite'
}

const RentalSettingDialog: FC<Props> = props => {
  const { className, groupId, rentalType, onClose, open } = props

  const { t } = useTranslation()

  const { config } = useFrontendConfigContext()

  const { getCurrency, getApeironPrice } = useCurrencyContext()

  const { fromWei } = useWeb3()

  const { orbitRental: rentalConfig } = config || {}

  const { loading, star, trackDetail } = useOrbitalTrackAttachContext()

  const { data: suggestedToken, fetch: fetchSuggestFee } =
    useGetOrbitalTrackSuggestFee()

  const { saveRentalSetting, deleteRentalSetting } = useSaveRentalSetting()

  const setting = useMemo(() => {
    if (rentalType === RentalType.INVITE) {
      return groupId
        ? R.find(
            R.propEq(groupId, 'inviteGroupId'),
            trackDetail?.inviteSettings || []
          )
        : null
    } else {
      return trackDetail?.openSetting
    }
  }, [
    groupId,
    trackDetail?.inviteSettings,
    trackDetail?.openSetting,
    rentalType
  ])

  const currency = getCurrency(setting?.currencyType || CurrencyTokenType.Usdc)

  const totalFee = useMemo((): number => {
    return R.isNotNil(setting) && R.isNotNil(currency)
      ? Number.parseFloat(fromWei(setting.rentalFee, currency.decimals))
      : 0
  }, [currency, fromWei, setting])

  const dailyFee = useMemo((): number => {
    if (R.isNil(setting) || setting.rentalDuration === 0) {
      return 0
    }

    const rentalFee = new Decimal(totalFee)

    const rentalDuration = new Decimal(setting.rentalDuration)

    return rentalFee.dividedBy(rentalDuration).toNumber()
  }, [setting, totalFee])

  const form = useForm<RentalSettingFieldValues>({
    mode: 'onChange',
    values: {
      tracksPerAddress:
        rentalType === RentalType.INVITE
          ? R.propOr(1, 'maxCountForEachAddress', setting)
          : 0,
      allowRenew: setting?.canBeRenew || false,
      whitelistAddresses:
        setting?.inviteAddressList?.map(address => ({ value: address })) || [],
      rentTrackCount: setting?.totalTrack || 1,
      rentDuration: setting?.rentalDuration || 0,
      rentFee: dailyFee,
      groupName: setting?.groupName || '',
      rentTotal: getApeironPrice(null, totalFee, {
        allowedLargeNumberAbbr: false
      })
    }
  })

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit: hookSubmit,
    getValues,
    reset,
    setValue,
    watch
  } = form

  const [agreed, setAgreed] = useState<boolean>(false)

  const suggestedFee = useMemo(() => {
    return getApeironPrice(suggestedToken?.type, suggestedToken?.amount, {
      maxDecimal: 2,
      maxLength: 5,
      allowedLargeNumberAbbr: false
    })
  }, [getApeironPrice, suggestedToken?.amount, suggestedToken?.type])

  const opened = setting?.totalTrack || 0 // track that assigned to public / private, but not attached planet yet

  const occupied = setting?.occupiedTrack || 0 // track that attached planet

  const notOpened = trackDetail?.availableOrbitalTrackCount || 0 // track that didn't assigned to public / private

  const minDuration = rentalConfig?.minDuration || 0

  const maxDuration = rentalConfig?.maxDuration || 0

  const tracksAttachedWithPlanet = useMemo(() => {
    return (
      trackDetail?.tracks.filter(
        track => track.planet != null && track.setting.inviteGroupId === groupId
      ) || []
    )
  }, [trackDetail?.tracks, groupId])

  const handleOnReset = useCallback(
    (success: boolean = false) => {
      reset()
      if (onClose) {
        onClose(success)
      }
    },
    [onClose, reset]
  )

  const handleOnClose = useCallback(() => {
    handleOnReset()
  }, [handleOnReset])

  const handleOnSuccess = useCallback(() => {
    handleOnReset(true)
  }, [handleOnReset])

  const handleOnDelete = useCallback(async () => {
    if (R.isNotNil(currency)) {
      const success = await deleteRentalSetting({
        groupId: groupId ? Number(groupId) : 0,
        starId: Number(star?.id),
        currency
      })

      if (onClose) {
        onClose(success)
      }
    }
  }, [groupId, star?.id, currency, deleteRentalSetting, onClose])

  const handleOnInputChange = useCallback(() => {
    const { rentFee, rentDuration } = getValues()

    const fee = isNaN(rentFee) ? 0 : rentFee

    const duration = isNaN(rentDuration) ? 0 : rentDuration

    const total = fee * duration

    setValue(
      'rentTotal',
      getApeironPrice(null, total, {
        maxLength: 10,
        maxDecimal: 6,
        allowedLargeNumberAbbr: false
      })
    )
  }, [getApeironPrice, getValues, setValue])

  const handleOnAgreeChange = useCallback(() => {
    setAgreed(!agreed)
  }, [agreed])

  const handleOnSubmit = useCallback(async () => {
    if (R.isNotNil(star) && R.isNotNil(trackDetail) && R.isNotNil(currency)) {
      const {
        allowRenew,
        groupName,
        rentDuration,
        rentFee,
        rentTrackCount,
        tracksPerAddress,
        whitelistAddresses
      } = getValues()

      if (
        rentalType === RentalType.INVITE &&
        (Number(tracksPerAddress) < 1 || whitelistAddresses.length < 1)
      ) {
        return
      }

      const success = await saveRentalSetting({
        addressList: whitelistAddresses.map(address => address.value),
        allowRenew,
        currency,
        groupId: groupId ? Number(groupId) : 0,
        groupName,
        rentDuration: Number(rentDuration),
        rentFee: Number(rentFee),
        rentTrackCount: Number(rentTrackCount),
        starId: Number(star.id),
        tracksPerAddress: Number(tracksPerAddress)
      })

      if (success) {
        handleOnSuccess()
      }
    }
  }, [
    star,
    trackDetail,
    getValues,
    saveRentalSetting,
    currency,
    groupId,
    rentalType,
    handleOnSuccess
  ])

  const watchTracksPerAddress = watch('tracksPerAddress', 0)

  const watchWhiteListAddresses = watch('whitelistAddresses')

  useEffect(() => {
    if (
      !R.isNil(star) &&
      featureAllowed(FeatureFlagType.ShowRentalSuggestedFee)
    ) {
      const buff = star.buff.agingBuff ?? 0

      const additionBuff = star.additionBuff.agingBuff ?? 0

      fetchSuggestFee({
        agingBuff: buff + additionBuff,
        currency: currency?.type || CurrencyTokenType.Usdc
      })
    }
  }, [star])

  useEffect(() => {
    if (!R.isNil(setting)) {
      setAgreed(true)
    }
  }, [setting])

  const trackNumberValid = !control.getFieldState('rentTrackCount').invalid

  return (
    <Popup className={className} open={open} onClose={handleOnClose}>
      <Head>
        <Title>{t(`godiverse.orbital_track.${rentalType}_track`)}</Title>
        <Description>
          {t(`godiverse.orbital_track.about_${rentalType}_track`)}
        </Description>
      </Head>
      {groupId != null && (
        <DeleteSection>
          <DeleteSectionLeftBox>
            <Typography>
              {t('godiverse.orbital_track.delete_setting_group_title', {
                context: rentalType === RentalType.PUBLIC ? 'open' : 'invite'
              })}
            </Typography>
            <Typography>
              {t('godiverse.orbital_track.delete_setting_group_description')}
            </Typography>
          </DeleteSectionLeftBox>
          <DeleteSectionRightBox>
            <DeleteButton
              disabled={tracksAttachedWithPlanet.length > 0 || loading}
              onClick={handleOnDelete}
            >
              {t('common.button.delete')}
            </DeleteButton>
          </DeleteSectionRightBox>
        </DeleteSection>
      )}
      <Form onSubmit={hookSubmit(handleOnSubmit)}>
        {rentalType === RentalType.INVITE && (
          <RentalSettingInput
            description={t('godiverse.orbital_track.set_invite_group_name')}
            input={<NameTextField control={control} name={'groupName'} />}
            loading={loading}
            title={t('godiverse.orbital_track.invite_track_name', 'Name')}
          />
        )}
        <RentalSettingInput
          description={
            <>
              <OrangeRentLabel>
                <Trans>
                  {t('godiverse.orbital_track.tracks_opened', {
                    count: opened || 0
                  })}
                </Trans>
              </OrangeRentLabel>
              <OrangeRentLabel>
                <Trans>
                  {t('godiverse.orbital_track.tracks_occupied', {
                    count: occupied || 0
                  })}
                </Trans>
              </OrangeRentLabel>
              <GreenRentLabel>
                <Trans>
                  {t('godiverse.orbital_track.tracks_available', {
                    count: notOpened || 0
                  })}
                </Trans>
              </GreenRentLabel>
            </>
          }
          input={
            <ApeironNumberTextField
              control={control}
              errorMessage={t('godiverse.orbital_track.track_count_error')}
              min={Math.max(occupied, 1)}
              max={opened + notOpened}
              name={'rentTrackCount'}
              wholeNumber
              remark={
                <MinMax>
                  <Trans>
                    {t('godiverse.orbital_track.min_max', {
                      min: Math.max(occupied, 1),
                      max: opened + notOpened
                    })}
                  </Trans>
                </MinMax>
              }
            />
          }
          loading={loading}
          title={t('godiverse.orbital_track.tracks_allowed')}
        />
        {rentalType === RentalType.INVITE && (
          <RentalSettingInput
            description={
              <Trans>
                {t('godiverse.orbital_track.invite_whitelist_address_count', {
                  count:
                    (watchWhiteListAddresses &&
                      form.getValues('whitelistAddresses')?.length) ||
                    0
                })}
                {t(
                  'godiverse.orbital_track.invite_whitelist_tracks_per_address',
                  {
                    count:
                      watchTracksPerAddress &&
                      form.getValues('tracksPerAddress')
                  }
                )}
              </Trans>
            }
            input={<WhitelistEdit disabled={!trackNumberValid} form={form} />}
            loading={loading}
            title={t(
              'godiverse.orbital_track.whitelist_popup.whitelist_wallet_address'
            )}
          />
        )}
        <RentalSettingInput
          description={t('godiverse.orbital_track.average_cost')}
          input={
            <ApeironNumberTextField
              control={control}
              helperText={t('godiverse.orbital_track.rental_fee_error')}
              name={'rentFee'}
              remark={
                featureAllowed(FeatureFlagType.ShowRentalSuggestedFee) &&
                Number(suggestedFee) !== 0 ? (
                  <Remark>
                    <Trans>
                      {t('godiverse.orbital_track.suggested_fee', {
                        fee: suggestedFee,
                        unit: currency?.name
                      })}
                    </Trans>
                  </Remark>
                ) : undefined
              }
              unit={<Unit>{currency?.name}</Unit>}
              onValueChanged={handleOnInputChange}
            />
          }
          loading={loading}
          title={t('godiverse.orbital_track.daily_fee')}
        />
        <RentalSettingInput
          description={t('godiverse.orbital_track.rent_time_limit', {
            min: minDuration,
            max: maxDuration
          })}
          input={
            <ApeironNumberTextField
              control={control}
              helperText={t('godiverse.orbital_track.rental_duration_error')}
              max={maxDuration}
              min={minDuration}
              name={'rentDuration'}
              unit={<Unit>{t('common.days')}</Unit>}
              wholeNumber
              onValueChanged={handleOnInputChange}
            />
          }
          loading={loading}
          title={t('godiverse.orbital_track.rent_duration')}
        />
        <RentalSettingInput
          description={t('godiverse.orbital_track.rent_charge', {
            count: (config?.orbitRental.fee || 0) * 100
          })}
          input={
            <ApeironNumberTextField
              control={control}
              InputProps={{ readOnly: true }}
              name={'rentTotal'}
              unit={<Unit>{currency?.name}</Unit>}
            />
          }
          loading={loading}
          title={t('godiverse.orbital_track.rent_total')}
        />
        <StyledApeironCheckbox
          checked={agreed}
          description={t('godiverse.orbital_track.attach_planet.checkbox', {
            count: rentalConfig?.cooldown || 0
          })}
          onChange={handleOnAgreeChange}
        />
        <AllowRenewSwitch
          hook={{
            control,
            name: 'allowRenew'
          }}
          label={t('godiverse.orbital_track.allow_renew')}
        />
        <ApplyButton disabled={!isDirty || !agreed || !isValid} type='submit'>
          {t('godiverse.orbital_track.apply_rental')}
        </ApplyButton>
      </Form>
    </Popup>
  )
}

type Props = {
  className?: string
  groupId?: string
  rentalType: RentalType
  open: boolean
  onClose?: (success: boolean) => void
}

export type RentalSettingFieldValues = {
  allowRenew: boolean
  groupName: string
  rentDuration: number
  rentFee: number
  rentTotal: string
  rentTrackCount: number
  tracksPerAddress: number
  whitelistAddresses: { value: string }[]
}

export default RentalSettingDialog
